import React, { useState, useEffect, useRef } from "react";
import {
  FaUserTie,
  FaHandshake,
  FaProjectDiagram,
  FaMicrochip,
  FaAward,
} from "react-icons/fa";
import { ArrowRight } from "lucide-react";

// Custom hook for the counting animation
const useCountingAnimation = (endValue, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const start = 0;
          const end = parseInt(endValue, 10);
          let startTime = null;

          const animateCount = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = (currentTime - startTime) / duration;
            const currentValue = Math.min(progress, 1) * (end - start) + start;
            setCount(Math.floor(currentValue));

            if (progress < 1) {
              requestAnimationFrame(animateCount);
            } else {
              setCount(end);
            }
          };

          requestAnimationFrame(animateCount);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [endValue, duration]);

  return [count, ref];
};

const stats = [
  {
    icon: <FaUserTie className="text-4xl" />,
    value: "4",
    label: "Years of Experience",
    color: "blue",
  },
  {
    icon: <FaHandshake className="text-4xl" />,
    value: "50",
    label: "Clients",
    color: "purple",
  },
  {
    icon: <FaProjectDiagram className="text-4xl" />,
    value: "100",
    label: "Projects Delivered",
    color: "teal",
  },
  {
    icon: <FaMicrochip className="text-4xl" />,
    value: "10",
    label: "Products Powered",
    color: "orange",
  },
  {
    icon: <FaAward className="text-4xl" />,
    value: "10",
    label: "Awards Won",
    color: "pink",
  },
];

const StatsBar = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">
            Our Achievements
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Numbers That Speak for Themselves
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Delivering excellence and building lasting relationships with every project
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {stats.map((item, index) => (
            <StatCard key={index} {...item} index={index} />
          ))}
        </div>
      </div>

      {/* Contact Us Button */}
      <div className="fixed right-0 top-1/2 transform -translate-y-1/2 z-50">
        <button className="group flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white py-3 px-5 rounded-l-xl shadow-lg hover:shadow-2xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform rotate-[-90deg] origin-bottom-right translate-x-12 translate-y-12 whitespace-nowrap">
          <span className="text-sm font-bold tracking-widest">CONTACT US</span>
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

// Separate component for each stat card
const StatCard = ({ icon, value, label, color, index }) => {
  const [animatedValue, ref] = useCountingAnimation(value);
  const [isHovered, setIsHovered] = useState(false);

  const colorClasses = {
    blue: {
      gradient: "from-blue-500 to-blue-600",
      icon: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
    },
    purple: {
      gradient: "from-purple-500 to-purple-600",
      icon: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
    },
    teal: {
      gradient: "from-teal-500 to-teal-600",
      icon: "text-teal-600",
      bg: "bg-teal-50",
      border: "border-teal-200",
    },
    orange: {
      gradient: "from-orange-500 to-orange-600",
      icon: "text-orange-600",
      bg: "bg-orange-50",
      border: "border-orange-200",
    },
    pink: {
      gradient: "from-pink-500 to-pink-600",
      icon: "text-pink-600",
      bg: "bg-pink-50",
      border: "border-pink-200",
    },
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <div
      ref={ref}
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        animationDelay: `${index * 100}ms`,
      }}
    >
      {/* Hover glow effect */}
      <div
        className={`absolute -inset-0.5 bg-gradient-to-r ${colors.gradient} rounded-2xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-500`}
      ></div>

      {/* Card */}
      <div className="relative bg-white rounded-2xl p-8 border border-gray-200 group-hover:border-gray-300 shadow-md group-hover:shadow-xl transition-all duration-300 transform group-hover:-translate-y-2">
        {/* Icon container */}
        <div
          className={`${colors.bg} ${colors.icon} w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 ${
            isHovered ? "scale-110 rotate-6" : ""
          }`}
        >
          {icon}
        </div>

        {/* Animated number */}
        <div
          className={`text-5xl font-black mb-2 bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}
        >
          {animatedValue}
          <span className="text-4xl">+</span>
        </div>

        {/* Label */}
        <p className="text-gray-600 font-medium text-sm leading-snug">{label}</p>

        {/* Bottom accent line */}
        <div
          className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${colors.gradient} rounded-b-2xl transition-all duration-300 ${
            isHovered ? "w-full" : "w-0"
          }`}
        ></div>
      </div>
    </div>
  );
};

export default StatsBar;